// packages
import JsSIP from 'jssip';
import { ref } from '@vue/composition-api';
import getDdi from '@/helpers/campaign';

// store
import store from '@/store';

// definição das configs
const configs = {
    ua: null,
    session: null,
    audio: new Audio()
};

/**
 * Iniciação do JsSIP.
 * Registo do utilizador no freeswitch e
 * validação se o registo foi realizado
 * com sucesso.
 *
 * @since 1.0.0
 * @category Função
 * @autor Tiago Martinho
 *
*/
const initJsSIP = () => {
    // fs_cli
    // show registrations
    // debug
    JsSIP.debug.enable( 'JsSIP:*' );

    // criação do sockect
    const socket = new JsSIP.WebSocketInterface(
        `wss://${process.env.VUE_APP_FREESWITCH_URL}:${process.env.VUE_APP_FREESWITCH_PORT}`
    );

    // obtenção das credenciais do utilizador logado.
    const { extension, accessPassword } = store.getters.freeswitchCredentials;

    // configurações do utilizador
    const configuration = {
        sockets: [ socket ],
        uri: `${extension}@${process.env.VUE_APP_FREESWITCH_IP}`,
        password: accessPassword,
        register: true,
        session_timers: false
    };

    // associado o JsSIP a uma var.
    configs.ua = new JsSIP.UA( configuration );

    const isRegisted = ref( null );

    // listeners JsSIP
    configs.ua.on( 'registrationFailed', event => {
        console.log( event );
        console.log( event.cause );
        isRegisted.value = false;
    });

    configs.ua.on( 'failed', event => {
        console.log( event.cause );
        isRegisted.value = false;
    });

    configs.ua.on( 'registered', event => {
        console.log( event );
        isRegisted.value = true;
    });

    // iniciação do registo.
    configs.ua.start();

    return { isRegisted };
};

/**
 * Iniciação de chamada.
 * Adicionado os eventos e opções de chamada e
 * caso a chamada seja sucesso, é adicionado os
 * audios ao browser.
 *
 * @since 1.0.0
 * @category Função
 * @autor Tiago Martinho
 *
*/
const call = ({ settings }) => {
    // caso já exista uma sessão anterior
    // e não foi terminada anteriormente
    if ( configs.session && !configs.session.isEnded() )
        hangUp();

    // eventos de chamada
    const eventHandlers = {
        progress () {
            console.log( 'call is in progress' );
        },
        failed ( e ) {
            console.log( `call failed with originator: ${e.originator}` );
            console.log( `call failed with message: ${e.message}` );
            console.log( `call failed with cause: ${e.cause}` );
            settings.end = true;
        },
        ended ( e ) {
            settings.end = true;
            console.log( `call ended with cause: ${e}` );
        },
        confirmed () {
            console.log( 'call confirmed' );
            settings.confirmed = true;
        },
        sdp ( e ) {
            console.log( `call sdp with originator: ${e.originator}` );
            console.log( `call sdp with message: ${e.type}` );
            console.log( `call sdp with cause: ${e.sdp}` );
        }
    };

    // opções da chamada
    const callOptions = {
        eventHandlers,
        mediaConstraints: { audio: true, video: false },
        ...( settings.id && {
            extraHeaders: [ `X-contact-call-id: ${settings.id}`, `X-ddi: ${getDdi({ ddis: settings.ddis }).value}` ] // dinamico
        }),
        pcConfig: {
            iceServers: [ {
                urls: [
                    'stun:stun.l.google.com:19302',
                    'stun:stun1.l.google.com:19302',
                    'stun:stun2.l.google.com:19302',
                    'stun:stun3.l.google.com:19302',
                    'stun:stun4.l.google.com:19302'
                ]
            } ]
        }
    };

    // iniciação da chamada.
    const session = configs.ua.call( settings.number, callOptions );

    // adicionado o audio ao browser.
    if ( session.connection ) {
        configs.session = session;
        console.log( 'Connection is valid' );

        session.connection.addEventListener( 'addstream', event => {
            console.log( 'Add stream' );
            configs.audio.srcObject = event.stream;
            configs.audio.play();
        });
    }
};

/**
 * Hold / unhold da chamada.
 *
 * @since 1.0.0
 * @category Função
 * @autor Tiago Martinho
 *
*/
const toggleHold = ({ isOnHold }) => ( isOnHold ? configs.session.unhold() : configs.session.hold() );

/**
 * Envio do tone para o freeswitch.
 *
 * @since 1.0.0
 * @category Função
 * @autor Tiago Martinho
 *
*/
const sendDTMF = tone => {
    configs.session.sendDTMF( tone );
};

/**
 * Desligar a chamada por parte
 * do utilizador.
 *
 * @since 1.0.0
 * @category Função
 * @autor Tiago Martinho
 *
*/
const hangUp = () => {
    configs.session.terminate();
};

/**
 * Logout do freeswitch.
 *
 * @since 1.0.0
 * @category Função
 * @autor Tiago Martinho
 *
*/
const logout = () => {
    const options = { all: true };
    configs.ua.unregister( options );
};

export {
    initJsSIP,
    call,
    toggleHold,
    hangUp,
    sendDTMF,
    logout
};
